import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { useDateAutoRefresh } from '@/hooks/useDateAutoRefresh';
import { useCelebrationStore } from '@/stores/celebrationStore';
import { Layout } from '@/components/layout/Layout';
import { Dashboard } from '@/pages/Dashboard';
import { Login } from '@/pages/Login';
import { Settings } from '@/pages/Settings';
import { RawData } from '@/pages/RawData';
import { Agents } from '@/pages/Agents';
import { CelebrationOverlay } from '@/components/celebration/CelebrationOverlay';
import type { CelebrationData } from '@/types';

// Dev-only: expose `window.__celebrate()` so the celebration overlay can
// be triggered from the browser console without a backend or WebSocket.
if (import.meta.env.DEV) {
  (window as unknown as { __celebrate?: (overrides?: Partial<CelebrationData>) => void }).__celebrate = (overrides = {}) => {
    useCelebrationStore.getState().trigger({
      agentName: 'טל',
      amount: 12500,
      companyName: 'בדיקה - חברה מנצחת',
      message: 'יששש טל אלוף עסקה של שתים עשרה אלף וחמש מאות שקלים מדהים',
      ...overrides,
    });
  };

  // Dev-only: trigger the one-time "We Are The Champions" 1M-ILS milestone
  // show (Queen song + dedicated video) straight from the console.
  (window as unknown as { __champions?: () => void }).__champions = () => {
    useCelebrationStore.getState().triggerChampion();
  };
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function WebSocketProvider({ children }: { children: React.ReactNode }) {
  useWebSocket();
  useTokenRefresh();
  useDateAutoRefresh(); // Auto-refresh date filters when day changes (for TV dashboards)
  return <>{children}</>;
}

function App() {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'he';

  return (
    <div className={isRtl ? 'font-hebrew' : 'font-sans'} dir={isRtl ? 'rtl' : 'ltr'}>
      <WebSocketProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/agents"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Agents />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/raw-data"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RawData />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <CelebrationOverlay />
      </WebSocketProvider>
    </div>
  );
}

export default App;
